/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Breadcrumbs, Container, Link as BCLink, Box, CircularProgress, Chip } from "@mui/material";
import { NavigateNext, CheckCircle, Error, Pause } from "@mui/icons-material";
import axios from "../../axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePermissionGuard from "../../hooks/usePermissionGuard";

const ENTITY_LABELS = {
  account: "Accounts", address: "Addresses", contact: "Contacts",
  item: "Products", sales_order: "Sales Orders", sales_order_line: "Order Lines",
  sales_invoice: "Invoices", sales_invoice_line: "Invoice Lines",
  sales_item_price: "Item Prices", sales_price_list_volume_discount: "Volume Discounts",
  stock_position: "Stock Positions", document: "Documents",
  document_attachment: "Attachments", deleted: "Deleted Records",
  item_group: "Item Groups", sales_price_list: "Price Lists",
  sales_price_list_linked_account: "Price List Accounts",
  sales_price_list_period: "Price List Periods",
  vat_code: "VAT Codes", unit: "Units",
  item_extra_field: "Item Extra Fields",
};

const toUtcDate = (d) => d ? new Date(d.endsWith('Z') ? d : d + ' UTC') : null;
const fmtDate = (d) => { const dt = toUtcDate(d); return dt ? dt.toLocaleString("nl-NL") : "Never"; };
const fmtAgo  = (d) => {
  if (!d) return "Never synced";
  const mins = Math.floor((Date.now() - toUtcDate(d)) / 60000);
  if (mins < 1)   return "Just now";
  if (mins < 60)  return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const StoreSyncStatus = () => {
  usePermissionGuard("view_sync_status");
  const [syncs, setSyncs] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get("/admin/store/dashboard/sync-status")
      .then(r => setSyncs(r.data.data))
      .catch(() => {});
  }, []);

  const totalRecords = syncs ? syncs.reduce((s, e) => s + e.count, 0) : 0;
  const staleCount   = syncs ? syncs.filter(e => e.enabled && !e.last_sync).length : 0;

  return (
    <div>
      <Container sx={{ pb: "2rem" }}>
        <div role="presentation">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />} sx={{ p: "14px 0" }}>
            <BCLink underline="none" color="inherit" href="/" fontSize="14px">{t("Home")}</BCLink>
            <BCLink underline="none" color="inherit" onClick={() => navigate("/store/dashboard")} sx={{ cursor: "pointer" }} fontSize="14px">Dashboard</BCLink>
            <BCLink underline="none" color="text.primary" aria-current="page" fontSize="14px">Sync Status</BCLink>
          </Breadcrumbs>
        </div>

        {!syncs ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress sx={{ color: "#cc9966" }} />
          </Box>
        ) : (
          <>
            {/* Summary bar */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
              {[
                { label: "Total Synced Records", value: totalRecords.toLocaleString(), color: "#1565c0" },
                { label: "Active Entities",       value: syncs.filter(e => e.enabled).length, color: "#2e7d32" },
                { label: "Never Synced",          value: staleCount, color: staleCount > 0 ? "#e53935" : "#999" },
              ].map(s => (
                <div key={s.label} style={{ flex: "1 1 160px", padding: "14px 18px", border: `1px solid ${s.color}30`, borderRadius: 8, background: s.color + "08" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </Box>

            {/* Entity table */}
            <div style={{ background: "#fff", border: "1px solid #ebebeb", borderRadius: 8, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "32%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#fafafa", borderBottom: "1px solid #ebebeb" }}>
                    {[
                      ["Entity",    "left"],
                      ["Status",    "center"],
                      ["Records",   "right"],
                      ["Last Sync", "left"],
                      ["Frequency", "left"],
                    ].map(([label, align]) => (
                      <th key={label} style={{
                        padding: "10px 16px", fontSize: 11, fontWeight: 600,
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        color: "#888", textAlign: align, whiteSpace: "nowrap",
                      }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {syncs.map(entity => {
                    const isStale = entity.enabled && !entity.last_sync;
                    const minsAgo = entity.last_sync
                      ? Math.floor((Date.now() - toUtcDate(entity.last_sync)) / 60000)
                      : null;
                    // Syncs dispatch one entity per division per cycle (per-division lock),
                    // so the real refresh cadence is the whole cycle (~15-25 min), not the
                    // per-entity interval. Only flag genuine staleness: well past the
                    // interval AND at least 30 min absolute.
                    const isOld = minsAgo !== null && minsAgo > Math.max((entity.frequent / 60) * 2.5, 30);

                    return (
                      <tr key={entity.type} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px 16px", fontWeight: 500, fontSize: 14, color: "#222" }}>
                          {ENTITY_LABELS[entity.type] || entity.type}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          {!entity.enabled ? (
                            <Chip icon={<Pause sx={{ fontSize: 13 }} />} label="Disabled" size="small"
                              sx={{ background: "#f5f5f5", color: "#999", fontSize: 11 }} />
                          ) : isStale ? (
                            <Chip icon={<Error sx={{ fontSize: 13 }} />} label="Never synced" size="small"
                              sx={{ background: "#ffebee", color: "#e53935", fontSize: 11 }} />
                          ) : isOld ? (
                            <Chip icon={<Error sx={{ fontSize: 13 }} />} label="Overdue" size="small"
                              sx={{ background: "#fff8e1", color: "#f57c00", fontSize: 11 }} />
                          ) : (
                            <Chip icon={<CheckCircle sx={{ fontSize: 13 }} />} label="OK" size="small"
                              sx={{ background: "#e8f5e9", color: "#2e7d32", fontSize: 11 }} />
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, fontSize: 14, color: "#222" }}>
                          {entity.count.toLocaleString()}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontSize: 13, color: "#333" }}>{fmtDate(entity.last_sync)}</div>
                          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{fmtAgo(entity.last_sync)}</div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#555" }}>
                          {entity.frequent
                            ? entity.frequent < 3600
                              ? `Every ${entity.frequent / 60}m`
                              : `Every ${entity.frequent / 3600}h`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default React.memo(StoreSyncStatus);
